import TextField from '@mui/material/TextField';

import { useDraft } from './draft';

/**
 * A number that saves what changed when it loses focus — the answer-key twin of
 * `EditableText`, GEN-49.
 *
 * Not `EditableText` with a `type` prop. The two differ in the one place that matters:
 * what counts as a non-edit. Text treats empty as a slip and puts the old value back;
 * a number has to treat *anything unparseable* the same way, and `0` — which is falsy,
 * and which a track legitimately starts at — as a real value.
 */
export function EditableNumber({
  label,
  value,
  suffix,
  disabled = false,
  onSave,
}: {
  label: string;
  /** `undefined` reads as an empty field: the column is null and wants a number. */
  value: number | undefined;
  /** The unit, shown rather than editable here: it belongs to the scene, not the field. */
  suffix?: string | undefined;
  disabled?: boolean;
  onSave: (next: number) => void;
}) {
  const { draft, setDraft, reset } = useDraft(value ?? '');

  return (
    <TextField
      label={suffix === undefined ? label : `${label} (${suffix})`}
      value={draft}
      size="small"
      fullWidth
      disabled={disabled}
      slotProps={{ htmlInput: { inputMode: 'decimal' } }}
      onChange={(event) => {
        setDraft(event.target.value);
      }}
      onBlur={() => {
        const next = Number(draft.trim());

        if (draft.trim() === '' || !Number.isFinite(next) || next === value) {
          reset();
          return;
        }

        onSave(next);
      }}
    />
  );
}
